import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setTokens, ApiError } from "../api";
import { moduloDisponible } from "../modulosCatalogo";

export type Usuario = {
  id: number; nombre: string; email: string; estado: string; dosFactorHabilitado: boolean;
};
export type SesionInfo = {
  usuario: Usuario;
  roles: { id: number; nombre: string }[];
  permisos: string[];
  empresa: { id: number; nombre: string } | null;
  // Licenciamiento por módulos (agosto 2026) — ver app/modulos_catalogo.py
  // y GET /api/auth/me. Lista de códigos de módulo activos y vigentes hoy
  // para la empresa de este usuario (independiente de sus permisos RBAC).
  modulosLicenciados: string[];
};

type AuthState = {
  cargando: boolean;
  sesion: SesionInfo | null;
  error: string | null;
  requiere2fa: boolean;
  /** Devuelve `true` si el inicio de sesión quedó completo (sesión activa) y
   *  `false` si lo que ocurrió fue pasar al paso de doble factor (aún no hay
   *  sesión). Así el llamador (Login.tsx) puede saber con certeza cuándo ya
   *  puede redirigir, sin depender del valor de `requiere2fa` en el render
   *  anterior (que todavía no reflejaría el cambio hecho dentro de login). */
  login: (email: string, password: string, totpCode?: string) => Promise<boolean>;
  logout: () => void;
  tienePermiso: (permiso: string) => boolean;
  /** true si la empresa de este usuario tiene contratado (y vigente) al
   *  menos uno de los módulos que habilitan esa ruta — ver
   *  modulosCatalogo.RUTA_MODULOS. Rutas no sujetas a licenciamiento (Core,
   *  Reportes) siempre devuelven true. */
  moduloDisponibleParaRuta: (ruta: string) => boolean;
  refrescarSesion: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [cargando, setCargando] = useState(true);
  const [sesion, setSesion] = useState<SesionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requiere2fa, setRequiere2fa] = useState(false);

  const cargarSesion = useCallback(async () => {
    try {
      const data = await api.get("/auth/me");
      setSesion(data);
    } catch {
      setSesion(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const restaurado = await api.restoreSession();
      if (restaurado) await cargarSesion();
      setCargando(false);
    })();
  }, [cargarSesion]);

  const login = useCallback(async (email: string, password: string, totpCode?: string) => {
    setError(null);
    try {
      const data = await api.post("/auth/login", { email, password, totpCode });
      if (data.requiere2fa) {
        setRequiere2fa(true);
        return false;
      }
      setRequiere2fa(false);
      setTokens(data.accessToken, data.refreshToken);
      await cargarSesion();
      return true;
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "No se pudo iniciar sesión.";
      setError(msg);
      throw e;
    }
  }, [cargarSesion]);

  const logout = useCallback(() => {
    setTokens(null, null);
    setSesion(null);
    setRequiere2fa(false);
    // Defensa adicional al bug de "se mantiene la página de EP al cambiar a
    // PR": el router es hash-based y no se resetea solo entre sesiones, así
    // que sin esto un logout desde SigeproEP deja el hash en "/sigepro-ep" y
    // la siguiente sesión (aunque entre en modo PR) arrancaría ahí. Se
    // resetea el hash directamente (sin acoplar este contexto a useRouter).
    if (window.location.hash && window.location.hash !== "#/") {
      window.location.hash = "/";
    }
  }, []);

  const tienePermiso = useCallback((permiso: string) => {
    if (!sesion) return false;
    const [modulo] = permiso.split(":");
    return sesion.permisos.includes(permiso) || sesion.permisos.includes(`${modulo}:*`) || sesion.permisos.includes("*:*");
  }, [sesion]);

  const moduloDisponibleParaRuta = useCallback((ruta: string) => {
    if (!sesion) return false;
    return moduloDisponible(ruta, sesion.modulosLicenciados);
  }, [sesion]);

  return (
    <AuthContext.Provider value={{ cargando, sesion, error, requiere2fa, login, logout, tienePermiso, moduloDisponibleParaRuta, refrescarSesion: cargarSesion }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
